import { deleteApi, getApi, postApi, putApi } from "./api-utils";

const toApiDate = (value) => {
  if (!value) return null;

  // Ant Design DatePicker returns a dayjs instance. Format it before Axios
  // serializes it to an ISO datetime, because the v4 API expects a date only.
  if (typeof value.format === "function") {
    return value.format("YYYY-MM-DD");
  }

  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  if (typeof value === "string") {
    const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return dateOnly ? dateOnly[1] : value;
  }

  return value;
};

const toLegacyDevice = (device) => ({
  ...device,
  creator: device.init_creator,
  init_date: device.init_at,
  group: device.in_group,
  type: device.type || "MASTER",
  status: Number(device.status),
  device_type: Number(device.device_type),
  users: device.users || [],
});

const toV4DevicePayload = (device) => ({
  qr_string: device.qr_string,
  status: String(device.status),
  device_type: String(device.device_type),
  lot: device.lot,
  init_creator: device.init_creator || device.user || null,
  imported_at: toApiDate(device.imported_at),
  tested_at: toApiDate(device.tested_at),
  in_group: device.in_group ?? device.group,
  note: device.note || null,
  coefficients: device.coefficients || null,
});

export const getWarehouseDevices = async ({ page = 1, pageSize = 100, search } = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (search) params.set("search", search);

  const response = await getApi(`api/v4/warehouses/devices?${params.toString()}`);
  return {
    ...response,
    data: (response.data || []).map(toLegacyDevice),
  };
};

export const getAllWarehouseDevices = async () => {
  const pageSize = 100;
  const firstPage = await getWarehouseDevices({ page: 1, pageSize });
  const allDevices = [...firstPage.data];
  const totalPages = Math.ceil((firstPage.total || allDevices.length) / pageSize);

  for (let page = 2; page <= totalPages; page += 1) {
    const response = await getWarehouseDevices({ page, pageSize });
    allDevices.push(...response.data);
  }

  return allDevices;
};

export const createWarehouseDevice = async (device) => {
  const response = await postApi("api/v4/warehouses/devices", toV4DevicePayload(device));
  return toLegacyDevice(response);
};

export const createWarehouseDevices = async (devices) => {
  const results = [];
  for (const [index, device] of devices.entries()) {
    try {
      results.push(await createWarehouseDevice(device));
    } catch (error) {
      error.deviceIndex = index;
      error.devicePayload = device;
      throw error;
    }
  }
  return results;
};

export const updateWarehouseDevice = async (device) => {
  const response = await putApi(`api/v4/warehouses/devices/${device.id}`, toV4DevicePayload(device));
  return toLegacyDevice(response);
};

export const resetWarehouseDevice = async (deviceId) => {
  const response = await postApi(`api/v4/warehouses/devices/${deviceId}/reset`, {});
  return response?.status === "success";
};

export const deleteWarehouseDevice = async (deviceId) => {
  return deleteApi(`api/v4/warehouses/devices/${deviceId}`);
};

export const buildWarehouseDeviceReport = (devices) => {
  const typeMap = {
    0: "S3",
    1: "S7",
    2: "S4",
    3: "LORA",
    4: "BLE",
    5: "GATEWAY",
  };
  const statusMap = {
    0: "init",
    1: "qc",
    2: "in-stock",
    3: "activated",
    4: "return",
  };

  const rowsByType = {};
  devices.forEach((device) => {
    const label = typeMap[Number(device.device_type)] || String(device.device_type);
    const status = statusMap[Number(device.status)] || String(device.status);
    rowsByType[label] = rowsByType[label] || { label, init: 0, qc: 0, "in-stock": 0, activated: 0, return: 0 };
    rowsByType[label][status] = (rowsByType[label][status] || 0) + 1;
  });

  return Object.values(rowsByType);
};
